import { getApiUrl } from '@/lib/api-config';
import { entityQueryParams, type EntityQuery } from '@/lib/entity-api';
import { uploadEntityAttachments, deleteAttachment } from '@/lib/entity-attachments';
import type { GenericAttachment } from '@/styles/GenericAttachmentsSection';

/**
 * Selections data layer. Everything goes through the backend API:
 *   - reads:  GET /api/selections (selections + options + DERIVED status,
 *             totals and counts + KPI summary; Builder Price only if the
 *             caller holds selections.view_builder_price)
 *   - select/deselect: PUT /api/selections/:id/options/:optionId/selected
 *   - create/update/delete of selections and options: the generic entity API
 * Nothing here talks to Supabase directly, and nothing computes status or
 * totals -- the server does, once, for List View, Card View and the KPIs.
 */

export type SelectionFileAttachment = GenericAttachment;

export interface SelectionOption {
  id: string;
  selection_id: string;
  name: string;
  description: string | null;
  link: string | null;
  client_price: number;
  /** Absent unless the caller may see it (selections.view_builder_price). */
  builder_price?: number;
  is_selected: boolean;
  attachment_count: number;
}

export type SelectionStatus = 'completed' | 'pending';

export interface Selection {
  id: string;
  selection_number: string;
  name: string;
  description: string | null;
  due_date: string | null;
  allowance: number;
  project_id: string;
  company_id: string;
  created_at: string;
  updated_at: string;
  accept_multiple_selections: boolean;
  sort_order: number;
  status: SelectionStatus;
  selected_option_ids: string[];
  selected_client_price: number;
  selected_builder_price?: number;
  attachment_count: number;
  comment_count: number;
  options: SelectionOption[];
}

export interface SelectionsSummary {
  total: number;
  selected: number;
  pending: number;
  client_total: number;
  builder_total?: number;
}

export interface SelectionsOverview {
  selections: Selection[];
  summary: SelectionsSummary;
  capabilities: { view_builder_price: boolean };
}

async function readJson(response: Response, fallback: string) {
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body?.error || body?.message || fallback);
  return body;
}

const jsonRequest = (method: string, body?: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  ...(body === undefined ? {} : { body: JSON.stringify(body) }),
});

/**
 * ONE page of selections from the aggregate read (options + derived state included) for an EntityQuery: search, sort,
 * project and paging happen on the server; `summary` (the KPI bar) describes EVERY selection the query matches.
 */
export async function fetchSelectionsPage(query: EntityQuery, page: { limit: number; offset: number }) {
  const body = await readJson(await fetch(getApiUrl(`api/selections?${entityQueryParams(query, page)}`)), 'Failed to load selections');
  const overview = body.data as SelectionsOverview;
  return {
    rows: overview.selections,
    total: Number(body.total ?? overview.selections.length),
    hasMore: body.has_more === true,
    extra: { summary: overview.summary, capabilities: overview.capabilities },
  };
}

/** EVERY selection the query matches, with its derived columns, straight from the backend (the CSV export). */
export async function exportSelectionRows(query: EntityQuery, ids?: string[]): Promise<Array<Record<string, any>>> {
  const body = await readJson(
    await fetch(getApiUrl('api/selections/export'), jsonRequest('POST', {
      ...(query.projectId ? { project_id: query.projectId } : {}),
      ...(query.search ? { search: query.search } : {}),
      ...(query.sort ? { sort: query.sort, dir: query.direction ?? 'asc' } : {}),
      ...(ids ? { ids } : {}),
    })),
    'Failed to export selections'
  );
  return body.data;
}

/** Every live selection of the project (or of the company), in the manual order -- what reordering arranges. */
export async function fetchSelectionsOverview(projectId?: string): Promise<SelectionsOverview> {
  const query = projectId ? `?project_id=${encodeURIComponent(projectId)}` : '';
  const body = await readJson(await fetch(getApiUrl(`api/selections${query}`)), 'Failed to load selections');
  return body.data as SelectionsOverview;
}

/** One selection by id with its derived state (a link from another document opens it); undefined when it no longer exists or the caller cannot read it. */
export async function fetchSelectionById(id: string): Promise<Selection | undefined> {
  try {
    const response = await fetch(getApiUrl(`api/selections/${encodeURIComponent(id)}`));
    if (!response.ok) return undefined;
    return (await response.json()).data as Selection;
  } catch (error) {
    console.error('Could not open the selection:', error);
    return undefined;
  }
}

/** Select (or, with selected=false, deselect) an option; returns the Selection's fresh derived state. */
export async function setOptionSelected(selectionId: string, optionId: string, selected: boolean): Promise<Selection> {
  const body = await readJson(
    await fetch(getApiUrl(`api/selections/${selectionId}/options/${optionId}/selected`), jsonRequest('PUT', { selected })),
    'Failed to update the selection'
  );
  return body.data as Selection;
}

/** Duplicate on the server: the selection, all its options and their files; nothing selected. */
export async function duplicateSelection(selectionId: string): Promise<{ selection: Selection; attachmentsFailed: number }> {
  const body = await readJson(await fetch(getApiUrl(`api/selections/${selectionId}/duplicate`), jsonRequest('POST')), 'Failed to duplicate selection');
  return body.data;
}

/**
 * The order to persist when only SOME selections were arranged (a project is
 * selected in the Project Selector): the arranged ones take, in their new
 * order, the positions they already occupied in the full list; every other
 * selection keeps its place. With no filter it is simply the new order.
 */
export function mergeSubsetOrder(fullIds: string[], arrangedIds: string[]): string[] {
  const arranged = new Set(arrangedIds);
  let next = 0;
  return fullIds.map((id) => (arranged.has(id) ? arrangedIds[next++] : id));
}

/** Persist the manual order (every selection, in order) in ONE request. */
export async function saveSelectionOrder(orderedIds: string[]): Promise<void> {
  await readJson(await fetch(getApiUrl('api/selections/order'), jsonRequest('PUT', { ordered_ids: orderedIds })), 'Failed to save the order');
}

export async function deleteSelections(ids: string[]): Promise<{ deleted: number; failed: number }> {
  let deleted = 0;
  let failed = 0;
  for (const id of ids) {
    const response = await fetch(getApiUrl(`api/entities/selection/${id}`), jsonRequest('DELETE'));
    if (response.ok) deleted++;
    else failed++;
  }
  return { deleted, failed };
}

/** The fields a Selection form edits (everything else is server-owned). */
export interface SelectionFields {
  name: string;
  description: string | null;
  due_date: string | null;
  allowance: number;
  project_id: string;
  accept_multiple_selections: boolean;
}

export async function createSelection(fields: SelectionFields): Promise<{ id: string }> {
  const body = await readJson(await fetch(getApiUrl('api/entities/selection'), jsonRequest('POST', fields)), 'Failed to create selection');
  return body.data;
}

/** A form-side option: `id` starts with `temp-` until it is saved. */
export interface OptionDraft {
  id: string;
  name: string;
  description: string;
  link: string;
  client_price: number;
}

export const isTempId = (id: string) => id.startsWith('temp-');

export const uploadSelectionOptionAttachments = (
  optionId: string | undefined,
  files: File[],
  companyId: string,
  userId: string
) => uploadEntityAttachments('selection_option', optionId, files, companyId, userId);

export const deleteSelectionOptionAttachment = (attachmentId: string, userId: string | undefined) =>
  deleteAttachment(attachmentId, userId);

function optionPayload(option: OptionDraft) {
  return {
    name: option.name.trim(),
    description: option.description.trim() || null,
    link: option.link.trim() || null,
    client_price: option.client_price,
  };
}

export interface SaveSelectionInput {
  selectionId?: string;
  fields: SelectionFields;
  options: OptionDraft[];
  removedOptionIds: string[];
}

export interface SaveSelectionResult {
  selectionId: string;
  /** option local id -> real id, for every option that now exists (used to upload staged files). */
  optionIds: Record<string, string>;
}

/**
 * Saves a Selection and its options through the API, in order: selection,
 * removed options, then each option (create new / update existing). Returns
 * the real ids so the form can upload the files that were waiting for them.
 * Throws on the first failure (nothing is half-hidden).
 */
export async function saveSelection(input: SaveSelectionInput): Promise<SaveSelectionResult> {
  let selectionId = input.selectionId;
  if (selectionId) {
    await readJson(await fetch(getApiUrl(`api/entities/selection/${selectionId}`), jsonRequest('PUT', input.fields)), 'Failed to update selection');
  } else {
    selectionId = (await createSelection(input.fields)).id;
  }

  for (const optionId of input.removedOptionIds) {
    if (isTempId(optionId)) continue;
    await readJson(await fetch(getApiUrl(`api/entities/selection_option/${optionId}`), jsonRequest('DELETE')), 'Failed to delete option');
  }

  const optionIds: Record<string, string> = {};
  for (const option of input.options) {
    if (isTempId(option.id)) {
      const body = await readJson(
        await fetch(getApiUrl('api/entities/selection_option'), jsonRequest('POST', { ...optionPayload(option), selection_id: selectionId })),
        'Failed to save option'
      );
      optionIds[option.id] = body.data.id;
    } else {
      await readJson(await fetch(getApiUrl(`api/entities/selection_option/${option.id}`), jsonRequest('PUT', optionPayload(option))), 'Failed to update option');
      optionIds[option.id] = option.id;
    }
  }
  return { selectionId, optionIds };
}

/** Saves the items of ONE option (its own, independent of the other options'): the server validates them and sets the option's builder price to their sum. */
export async function saveSelectionOptionLines(optionId: string, lines: Array<Record<string, unknown>>): Promise<void> {
  await readJson(await fetch(getApiUrl(`api/selection-options/${optionId}/lines`), jsonRequest('PUT', { lines })), 'Failed to save the items of the option');
}
